import React, { useState } from 'react';
import instance from '../redux/actions/AxiosInstance/authenticated';

const CancellationModal = ({ 
  showModal, 
  onClose, 
  planId, 
  isLoading, 
  setIsLoading,
  onSuccess = () => {}
}) => {
  const [cancelStep, setCancelStep] = useState(1);
  const [cancelReason, setCancelReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [cancellationResult, setCancellationResult] = useState(null);

  // Reset modal state when closed
  const resetModal = () => {
    setCancelStep(1);
    setCancelReason("");
    setFeedback("");
    setCancellationResult(null);
    onClose();
  };

  const cancelSubscription = async (planId) => {
    try {
      setIsLoading(planId);
      if (!isLoading) {
        const res = await instance.post(
          process.env.REACT_APP_BASE_URL + "/subscription/cancel",
          {
            cancellation_reason: {
              reason: cancelReason,
              feedback,
            },
          }
        );
        setCancellationResult(true);
        setCancelStep(4);
        onSuccess(); // Call success callback
      }
    } catch (error) {
      console.log("error", error);
      setCancellationResult(false);
      setCancelStep(4);
    } finally {
      setIsLoading(null);
    }
  };

  const handleGoToDashboard = () => {
    resetModal();
    window.location.href = "/dashboard";
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
      <div className="bg-slate-800 rounded-xl shadow-2xl p-8 w-full max-w-md mx-4 relative">
        {/* Close Button - Hide on step 4 */}
        {cancelStep !== 4 && (
          <button
            onClick={resetModal}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Back Button - Hide on step 4 */}
        {cancelStep > 1 && cancelStep !== 4 && (
          <button
            onClick={() => setCancelStep(cancelStep - 1)}
            className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* Step 1: Why are you cancelling? */}
        {cancelStep === 1 && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-6">
              Why Are You Cancelling?
            </h2>
            <div className="space-y-3 text-left">
              {[
                "I'm not making sales",
                "Found a better tool for Amazon",
                "Too expensive for me",
                "The app isn't working as expected",
                "Other Reason",
              ].map((reason) => (
                <label
                  key={reason}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    cancelReason === reason
                      ? "border-green-500 bg-green-500/10"
                      : "border-gray-600 hover:border-green-400 hover:bg-green-400/5"
                  }`}
                >
                  <input
                    type="radio"
                    name="cancelReason"
                    value={reason}
                    checked={cancelReason === reason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="mr-3 text-green-500 focus:ring-green-500"
                  />
                  <span className="text-gray-200">{reason}</span>
                </label>
              ))}
            </div>
            <button
              onClick={() => setCancelStep(2)}
              disabled={!cancelReason}
              className="w-full mt-6 py-3 px-6 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2: Feedback */}
        {cancelStep === 2 && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Please Provide Us More Information
            </h2>
            <p className="text-gray-400 mb-6">
              We want to create subscriptions our customers find valuable -
              let us know what we could do better.
            </p>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Your feedback helps us improve."
              className="w-full h-32 p-4 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 resize-none focus:border-green-500 focus:outline-none"
            />
            <button
              onClick={() => setCancelStep(3)}
              disabled={!feedback.trim()}
              className={`w-full mt-6 py-3 px-6 text-white font-semibold rounded-lg transition-colors ${
                !feedback.trim()
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              Submit Feedback
            </button>
          </div>
        )}

        {/* Step 3: Final Confirmation */}
        {cancelStep === 3 && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              We'll Really Miss You
            </h2>
            <p className="text-gray-400 mb-6">
              Thank you for being part of our journey. We're sorry to see
              you go, but we understand. Your feedback will help us improve
              our service for future users.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => cancelSubscription(planId)}
                disabled={isLoading}
                className="w-full py-3 px-6 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
              >
                {isLoading ? "Cancelling..." : "Cancel Subscription"}
              </button>
              <button
                onClick={resetModal}
                disabled={isLoading}
                className="w-full py-3 px-6 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
              >
                Keep My Subscription
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Cancellation Result */}
        {cancelStep === 4 && (
          <div className="text-center">
            {cancellationResult ? (
              <>
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    Your Subscription is Cancelled
                  </h2>
                  <p className="text-gray-400">
                    Your subscription has been successfully cancelled. We're
                    sorry to see you go!
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="mb-6">
                  <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    Something Went Wrong
                  </h2>
                  <p className="text-gray-400">
                    We couldn't process your cancellation request. Please
                    contact our support team for assistance.
                  </p>
                </div>
              </>
            )}
            <button
              onClick={handleGoToDashboard}
              className="w-full py-3 px-6 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancellationModal;
